import AdvancedSearch from '@app/components/AdvancedSearch'
import InfiniteScrollTable from '@app/components/InfiniteScrollTable'
import SearchBar from '@app/components/SearchBar'
import { IGoal } from '@app/models/Goal'
import { IGlobalConfigurationState } from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import { formatDate, humanize } from '@app/utils/formatting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Column } from 'react-virtualized'
import { Col, FormGroup, Input, Label } from 'reactstrap'
import { actionCreators, IGoalsListState } from '../modules/goalsList'

type ComponentProps = IGoalsListState &
  IGlobalConfigurationState &
  typeof actionCreators

export default class AssignmentsListPage extends React.PureComponent<
  ComponentProps
> {
  private tableRef: React.RefObject<InfiniteScrollTable>

  constructor(props: ComponentProps) {
    super(props)

    bindFunctions(this)

    this.tableRef = React.createRef<InfiniteScrollTable>()
  }

  public render() {
    return (
      <DocumentTitle title='Goals'>
        <div className='container d-flex flex-column h-100 pt-3'>
          <div>
            <h3>My Goals</h3>
            <SearchBar
              onChange={this._onChangeFilter}
              placeholder='Looking for something?'
              size='sm'
              value={this.props.filterText}
            />
            <AdvancedSearch className='mt-1' expand='md'>
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Date</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.dueDate}
                    onChange={this._onChangeDueDate}
                  >
                    <option key='date1' value='All'>
                      All
                    </option>
                    {this.props.configuration.assignmentDueAdvancedOptions
                      .map((c, i) => (
                        <option key={`date-${i}`} value={c}>
                          {humanize(c)}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Type</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.goalType}
                    onChange={this._onChangeGoalType}
                  >
                    <option key='type1' value='All'>
                      All
                    </option>
                    {this.props.configuration.goalTypeOptions
                      .map((c, i) => (
                        <option key={`type-${i}`} value={c}>
                          {humanize(c)}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4} xl={3}>
                <FormGroup>
                  <Label>Status</Label>
                  <Input
                    type='select'
                    bsSize='sm'
                    value={this.props.status}
                    onChange={this._onChangeStatus}
                  >
                    {this.props.configuration.assignmentStatuses
                      .map((c, i) => (
                        <option key={`status-${i}`} value={c}>
                          {humanize(c)}
                        </option>
                      ))
                      .toJS()}
                  </Input>
                </FormGroup>
              </Col>
            </AdvancedSearch>
          </div>
          <div className='mt-4'>
            <InfiniteScrollTable
              isLoading={this.props.isLoading}
              items={this.props.items}
              rowHeight={50}
              loadItems={this.props.loadGoals}
              noRowsRenderer={this._noRowsRenderer}
              onRowClicked={this._onRowClicked}
              totalItems={this.props.totalItems}
              ref={this.tableRef}
            >
              <Column
                cellDataGetter={({ rowData }) => {
                  const goal = rowData as IGoal
                  return goal.text
                }}
                dataKey='title'
                flexGrow={1}
                label='Title'
                width={100}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const goal = rowData as IGoal
                  return goal.dueDate
                    ? formatDate(goal.dueDate, 'MM/DD/YYYY')
                    : 'No Due Date'
                }}
                dataKey='dueDate'
                flexGrow={1}
                label='Due Date'
                width={100}
              />
              <Column
                cellDataGetter={({ rowData }) => {
                  const goal = rowData as IGoal
                  return goal.type
                }}
                dataKey='goalType'
                flexGrow={1}
                label='Type'
                width={100}
              />
              <Column
                cellRenderer={({ rowData }) => {
                  const goal = rowData as IGoal
                  return (
                    <div
                      onClick={(e) => {
                        // clicked goal text
                      }}
                    >
                      <FontAwesomeIcon
                        icon={['fal', 'trash']}
                        color='#D33921'
                      />
                    </div>
                  )
                }}
                dataKey='deleteButton'
                width={30}
              />
            </InfiniteScrollTable>
          </div>
        </div>
      </DocumentTitle>
    )
  }

  private _noRowsRenderer() {
    return <div>No goals</div>
  }

  private _onRowClicked(item: IGoal, index: number) {
    window.alert(`Clicked on ${item.text}`)
  }

  private _onChangeDueDate(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setDueDate(event.target.value)
    this._reloadData()
  }

  private _onChangeFilter(newFilter: string) {
    this.props.setFilterText(newFilter)
    this._reloadData()
  }

  private _onChangeGoalType(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setGoalType(event.target.value)
    this._reloadData()
  }

  private _onChangeStatus(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setStatus(event.target.value)
    this._reloadData()
  }

  private _onChangeSortOrder(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setSortOrder(event.target.value)
    this._reloadData()
  }

  private _reloadData() {
    if (this.tableRef.current) {
      this.tableRef.current.forceReloadData()
    }
  }
}
